import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Monitor, Clock, FileText, MousePointerClick } from "lucide-react";
import api from "../api/client";

export default function BehaviorReplay({ selectedSite }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/behavior/sessions/${id}/replay/`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  const eventIcon = (type) => {
    switch (type) {
      case "click":
        return <MousePointerClick size={12} className="text-copper" />;
      case "pageview":
        return <FileText size={12} className="text-blue-400" />;
      case "scroll":
        return <Monitor size={12} className="text-green-400" />;
      case "exit_intent":
        return <Monitor size={12} className="text-red-400" />;
      default:
        return <Monitor size={12} className="text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/behavior/sessions")}
          className="p-2 hover:bg-dark-50/50 rounded-lg transition-colors"
        >
          <ArrowLeft size={18} className="text-gray-400" />
        </button>
        <div>
          <h2 className="text-lg font-semibold">Session Replay</h2>
          <p className="text-xs text-gray-500 font-mono">{id}</p>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-16 bg-dark-300 rounded-xl" />
          <div className="h-96 bg-dark-300 rounded-xl" />
        </div>
      ) : !data ? (
        <div className="text-gray-500 text-center py-20">
          Session not found
        </div>
      ) : (
        <>
          {/* Session info bar */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-gray-500" />
              <span className="text-sm text-gray-300">
                Duration: {data.duration ? `${data.duration.toFixed(0)}s` : "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-gray-500" />
              <span className="text-sm text-gray-300">
                Pages: {data.page_count || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor size={14} className="text-gray-500" />
              <span className="text-sm text-gray-300 capitalize">
                Device: {data.device_type || "—"}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {data.started_at
                ? new Date(data.started_at).toLocaleString()
                : ""}
            </div>
          </div>

          {/* rrweb player placeholder */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-8 flex flex-col items-center justify-center min-h-[400px]">
            <Monitor size={48} className="text-gray-600 mb-4" />
            <p className="text-gray-400 text-sm text-center">
              Recording replay requires rrweb-player library
            </p>
            <p className="text-gray-600 text-xs mt-2 text-center">
              Install rrweb-player to enable visual session replay
            </p>
          </div>

          {/* Event timeline */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-dark-50">
              <h3 className="text-sm font-semibold text-gray-300">
                Event Timeline
              </h3>
            </div>
            <div className="divide-y divide-dark-50/50 max-h-96 overflow-y-auto">
              {(data.events || []).map((event, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-dark-50/30"
                >
                  {eventIcon(event.type)}
                  <span className="text-xs text-gray-300 capitalize flex-1">
                    {event.type.replace("_", " ")}
                    {event.url && (
                      <span className="text-gray-500 ml-2 font-mono">
                        {event.url}
                      </span>
                    )}
                    {event.selector && (
                      <span className="text-gray-500 ml-2 font-mono">
                        {event.selector}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500 font-mono">
                    {event.timestamp
                      ? new Date(event.timestamp).toLocaleTimeString()
                      : `+${event.offset || 0}s`}
                  </span>
                </div>
              ))}
              {(!data.events || !data.events.length) && (
                <div className="px-5 py-8 text-center text-gray-500 text-sm">
                  No events recorded
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
